const SCOPE = {
  institution: "university-wide (all programmes), not this course",
  institution_level: "university-wide for this qualification type, not this course",
  institution_field: "this university, broad subject field, not this course",
  province_field: "province-wide for this field (all institutions), not this course",
};

const money = (v, cur) => (v == null ? null : `${cur} ${Math.round(v).toLocaleString("en-GB")}`);

function horizon(m) {
  if (m.horizon_years) return `${m.horizon_years} year${m.horizon_years > 1 ? "s" : ""} after ${m.reference_event}`;
  return m.horizon;
}

function IntlMeasure({ m, testId }) {
  return (
    <div data-testid={testId} className="rounded border border-stone-200 bg-white p-2">
      <div className="text-[10px] uppercase tracking-wider text-stone-500">{horizon(m)} · {m.period}</div>
      <div className="font-chip text-lg font-semibold text-stone-900">
        {m.value != null ? money(m.value, m.currency) : <span className="text-sm text-stone-500">Not published (suppressed)</span>}
      </div>
      <div className="text-[10px] text-stone-500">{m.measure} · {m.level}</div>
      {m.cohort && <div className="text-[10px] text-stone-500">{m.cohort}</div>}
      {m.annualised_calc && <div data-testid={`${testId}-annualised`} className="text-[10px] text-stone-500">{money(m.annualised_calc.value, m.currency)} a year ({m.annualised_calc.label})</div>}
      <details className="mt-1 text-[10px] text-stone-500">
        <summary className="cursor-pointer text-stone-600">Source details</summary>
        <div>{m.source} · {m.field} · {m.row}</div>
        <div>Population: {m.population}{m.count != null ? ` (n = ${m.count})` : ""}</div>
        {m.cohort && <div>Cohort: {m.cohort}</div>}
        {m.price_basis && <div>Price basis: {m.price_basis}</div>}
        {m.caveat && <div>Note: {m.caveat}</div>}
        <div>Licence: {m.licence} · fingerprint {String(m.raw_sha256 || "").slice(0, 12)}</div>
      </details>
    </div>
  );
}

export const IntlEarningsPanel = ({ official }) => {
  if (!official) return null;
  const prog = (official.measures || []).filter((m) => m.key !== "us_fos_4y" || m.value != null);
  const ctx = (official.context || []).filter((m) => m.value != null);
  const withheld = Object.entries(official.withheld || {});
  const review = official.pending_review || [];
  const groups = ctx.reduce((g, m) => ({ ...g, [m.scope || "institution"]: [...(g[m.scope || "institution"] || []), m] }), {});
  return (
    <div data-testid="intl-earnings-panel" className="mt-4 rounded-lg border border-stone-200 bg-[#FDFBF7] p-4">
      <div className="text-[11px] uppercase tracking-wider text-stone-500">Graduate earnings from official sources</div>
      {official.status !== "official" && (
        <div data-testid="intl-earnings-unavailable" className="mt-1 font-chip text-base font-semibold text-stone-600">{official.label}</div>
      )}
      {prog.length > 0 && (
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {prog.map((m) => <IntlMeasure key={m.key} m={m} testId={`intl-earnings-${m.key}`} />)}
        </div>
      )}
      {Object.entries(groups).map(([scope, ms]) => (
        <div key={scope} data-testid={`intl-earnings-ctx-group-${scope}`} className="mt-3">
          <div className="text-[10px] uppercase tracking-wider text-stone-500">Context: {SCOPE[scope] || scope}</div>
          <div className="mt-1 grid gap-2 sm:grid-cols-2">
            {ms.map((m) => <IntlMeasure key={m.key} m={m} testId={`intl-earnings-ctx-${m.key}`} />)}
          </div>
        </div>
      ))}
      {review.length > 0 && (
        <div data-testid="intl-earnings-pending-review" className="mt-3 text-xs text-stone-600">
          {review.map((r, i) => <div key={i}>Pending review: {r.label}{r.detail ? ` (${r.detail})` : ""}</div>)}
        </div>
      )}
      {withheld.length > 0 && (
        <details data-testid="intl-earnings-withheld" className="mt-3 text-xs text-stone-500">
          <summary className="cursor-pointer">Figures not shown ({withheld.length})</summary>
          <ul className="mt-1 list-disc pl-4">
            {withheld.map(([p, r]) => <li key={p}><span className="font-mono break-all">{p}</span>: {r}</li>)}
          </ul>
        </details>
      )}
    </div>
  );
};
